import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { CalendarDays, Plus, ChevronLeft, ChevronRight, Clock, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const DAYS_TR = ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'];
const CONTENT_TYPES = [
  { value: 'caption', label: 'Başlık' }, { value: 'blog_outline', label: 'Blog' },
  { value: 'ad_copy', label: 'Reklam' }, { value: 'short_script', label: 'Senaryo' },
  { value: 'video', label: 'Video' }, { value: 'image', label: 'Görsel' },
];
const STATUSES = { draft: 'Taslak', scheduled: 'Planlandı', published: 'Yayınlandı', cancelled: 'İptal' };
const STATUS_COLORS = {
  draft: 'bg-[#737373]/10 text-[#A3A3A3] border-[#525252]/20',
  scheduled: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  published: 'bg-green-500/10 text-green-400 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
};

export default function ContentCalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({ title: '', content_type: 'caption', platform: 'instagram', notes: '', scheduled_at: '' });

  const load = () => {
    const start = new Date(year, month, 1).toISOString();
    const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
    api.get(`/calendar?start=${start}&end=${end}`)
      .then(r => { setEvents(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [year, month]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const openCreate = (day) => {
    const d = new Date(year, month, day);
    setSelectedDate(d);
    setForm({ title: '', content_type: 'caption', platform: 'instagram', notes: '', scheduled_at: d.toISOString().slice(0, 16) });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!form.title) { toast.error('Başlık gerekli'); return; }
    try {
      await api.post('/calendar', { ...form, status: 'scheduled' });
      toast.success('İçerik planlandı');
      setShowCreate(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/calendar/${id}`, { status });
      toast.success(status === 'published' ? 'Yayınlandı olarak işaretlendi' : 'Durum güncellendi');
      load();
    } catch { toast.error('Güncelleme başarısız'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/calendar/${id}`); toast.success('Silindi'); load(); }
    catch { toast.error('Silme başarısız'); }
  };

  // Calendar grid
  const firstDay = new Date(year, month, 1).getDay();
  const adjustedFirst = firstDay === 0 ? 6 : firstDay - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < adjustedFirst; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getEventsForDay = (day) => {
    if (!day) return [];
    return events.filter(e => {
      const d = new Date(e.scheduled_at);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };

  return (
    <div data-testid="content-calendar-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">İçerik Takvimi</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">İçeriklerinizi planlayın ve takip edin</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={prevMonth} className="text-[#A3A3A3] hover:text-white h-9 w-9 p-0" data-testid="cal-prev"><ChevronLeft size={18} /></Button>
          <h2 className="font-outfit text-xl text-white font-medium w-48 text-center">{MONTHS_TR[month]} {year}</h2>
          <Button variant="ghost" onClick={nextMonth} className="text-[#A3A3A3] hover:text-white h-9 w-9 p-0" data-testid="cal-next"><ChevronRight size={18} /></Button>
        </div>
        <div className="flex gap-3">
          {Object.entries(STATUSES).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${k === 'draft' ? 'bg-[#737373]' : k === 'scheduled' ? 'bg-blue-400' : k === 'published' ? 'bg-green-400' : 'bg-red-400'}`} />
              <span className="text-xs text-[#737373] font-ibm">{v}</span>
            </div>
          ))}
        </div>
      </div>
      <Card className="bg-[#141414] border-[#262626]">
        <CardContent className="p-0">
          <div className="grid grid-cols-7">
            {DAYS_TR.map(d => (
              <div key={d} className="p-3 text-center text-xs font-ibm text-[#737373] border-b border-[#262626]">{d}</div>
            ))}
            {cells.map((day, i) => {
              const dayEvents = getEventsForDay(day);
              const isToday = day && new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
              return (
                <div
                  key={i}
                  className={`min-h-[100px] p-2 border-b border-r border-[#262626] transition-colors duration-200 ${day ? 'hover:bg-white/5 cursor-pointer' : 'bg-[#0A0A0A]/50'}`}
                  onClick={() => day && openCreate(day)}
                  data-testid={day ? `cal-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <div className={`text-xs font-ibm mb-1 ${isToday ? 'text-[#FACC15] font-bold' : 'text-[#A3A3A3]'}`}>{day}</div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map(ev => (
                          <div
                            key={ev.id}
                            className="group flex items-center gap-1 p-1 rounded text-xs bg-[#0A0A0A] border border-[#1A1A1A]"
                            onClick={e => e.stopPropagation()}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${ev.status === 'published' ? 'bg-green-400' : ev.status === 'scheduled' ? 'bg-blue-400' : 'bg-[#737373]'}`} />
                            <span className="text-[#A3A3A3] truncate flex-1 font-ibm">{ev.title}</span>
                            <button onClick={() => handleStatusChange(ev.id, 'published')} className="opacity-0 group-hover:opacity-100 text-green-400 hover:text-green-300" title="Yayınla"><Check size={10} /></button>
                            <button onClick={() => handleDelete(ev.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300" title="Sil"><Trash2 size={10} /></button>
                          </div>
                        ))}
                        {dayEvents.length > 3 && <span className="text-xs text-[#525252]">+{dayEvents.length - 3} daha</span>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader><DialogTitle className="font-outfit text-white">İçerik Planla</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Başlık *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="cal-title-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="İçerik başlığı..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">İçerik Türü</Label>
                <Select value={form.content_type} onValueChange={v => setForm({...form, content_type: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="cal-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {CONTENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm({...form, platform: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="cal-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {['instagram','twitter','facebook','linkedin','tiktok','youtube'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Tarih ve Saat</Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} data-testid="cal-datetime-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Notlar</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="cal-notes-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Planlama notları..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handleCreate} data-testid="cal-create-btn" className="bg-white text-black hover:bg-[#E5E5E5]">Planla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
